import React, { type ComponentProps } from 'react';
import { Empty as AntdEmpty } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTheme } from '@emotion/react';
import { FormattedMessage } from 'react-intl';
import EmptyStateLayout from 'components/common/empty-state-layout';

// antd's props type omits `data-e2e`, so the new-design branch has to forward it by hand.
export type EmptyProps = ComponentProps<typeof AntdEmpty> & { 'data-e2e'?: string };

// antd Empty is embedded (cards, a 200px VCS panel, table bodies), so the new design passes `icon`,
// not `illustration`. APO-484 deletes the branch.

const BridgedEmptyComponent: React.FunctionComponent<EmptyProps> = props => {
  const theme = useTheme();

  if (!theme.isNewDesign) {
    return <AntdEmpty {...props} />;
  }

  return (
    <EmptyStateLayout
      icon={<InboxOutlined />}
      description={props.description ?? <FormattedMessage id="no.data" />}
      action={props.children}
      className={props.className}
      data-e2e={props['data-e2e'] ?? 'empty'}
    />
  );
};

// Here because this is the file the lint rule lets import antd's Empty; antd-wrapper picks who gets it.
export const CompactEmpty: React.FunctionComponent = () => (
  <AntdEmpty image={AntdEmpty.PRESENTED_IMAGE_SIMPLE} className="ant-empty-small" />
);

export const BridgedEmpty = Object.assign(BridgedEmptyComponent, {
  PRESENTED_IMAGE_SIMPLE: AntdEmpty.PRESENTED_IMAGE_SIMPLE,
  PRESENTED_IMAGE_DEFAULT: AntdEmpty.PRESENTED_IMAGE_DEFAULT
});

export default BridgedEmpty;
